// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Which code points charsight looks at, and what each one is for.
 *
 * WHY NOT JUST `Default_Ignorable_Code_Point`. It is the obvious master list and it is the wrong
 * one, in both directions:
 *
 *  · It OVER-shoots. Roughly half of it has a legitimate use. U+FE0F is emoji presentation,
 *    U+200C is orthographically mandatory in Persian, U+200D is structural in 1,620 emoji
 *    sequences and in Indic conjuncts, U+115F/U+1160/U+3164 are real Hangul jamo fillers,
 *    U+180B..U+180F are real Mongolian, U+00AD is real hyphenation, U+E0100.. are registered
 *    ideographic variation sequences. "Remove everything Default_Ignorable" corrupts Persian,
 *    Indic, Korean, Mongolian and Japanese text and visibly breaks emoji for everybody.
 *
 *  · It UNDER-shoots, and it under-shoots on exactly the characters people actually come here for.
 *    White_Space is explicitly SUBTRACTED from the derivation, which excludes U+00A0, U+202F and
 *    the whole U+2000..U+200A family — the number-one cause of the broken CSV, the failing YAML
 *    parse and the shell command that will not run. It also excludes U+FFF9..U+FFFB interlinear
 *    annotation, U+2800 BRAILLE PATTERN BLANK, every private-use code point, every C0/C1 control,
 *    every noncharacter, and every homoglyph — homoglyphs being visible, which is a different lens
 *    entirely.
 *
 * So Default_Ignorable is carried as an ATTRIBUTE of a finding ("renders as nothing in conforming
 * software", which is precisely what the property means and precisely as far as it goes), and the
 * candidate set is built explicitly below.
 */

import {
  generalCategory,
  isDeprecated,
  isNoncharacter,
  isPrependedMark,
} from '../data/unicode';
import type { CategoryId, Tier } from './types';

export const TAG_BLOCK_START = 0xe0000;
export const TAG_BLOCK_END = 0xe00ff;
export const TAG_ASCII_START = 0xe0020;
export const TAG_ASCII_END = 0xe007e;
export const TAG_LANGUAGE = 0xe0001;
export const TAG_CANCEL = 0xe007f;

export const VS_BMP_START = 0xfe00;
export const VS_BMP_END = 0xfe0f;
export const VS_SUP_START = 0xe0100;
export const VS_SUP_END = 0xe01ef;

/**
 * The only lawful use of the Tags block in all of Unicode: the three RGI emoji tag sequences.
 *
 * Allowlisted as EXACT full sequences, never as the pattern "U+1F3F4 followed by tag characters" —
 * a black flag plus arbitrary tag text plus CANCEL TAG renders as a bare black flag and is a known
 * hiding place.
 */
export const RGI_TAG_SEQUENCES: readonly (readonly number[])[] = [
  [0x1f3f4, 0xe0067, 0xe0062, 0xe0065, 0xe006e, 0xe0067, 0xe007f], // flag: England  ("gbeng")
  [0x1f3f4, 0xe0067, 0xe0062, 0xe0073, 0xe0063, 0xe0074, 0xe007f], // flag: Scotland ("gbsct")
  [0x1f3f4, 0xe0067, 0xe0062, 0xe0077, 0xe006c, 0xe0073, 0xe007f], // flag: Wales    ("gbwls")
];

/** Every Zs except U+0020, mapped to the ASCII space they are usually standing in for. */
export const SPACE_LIKE = new Map<number, string>([
  [0x00a0, ' '], // NO-BREAK SPACE
  [0x1680, ' '], // OGHAM SPACE MARK
  [0x2000, ' '],
  [0x2001, ' '],
  [0x2002, ' '],
  [0x2003, ' '],
  [0x2004, ' '],
  [0x2005, ' '],
  [0x2006, ' '],
  [0x2007, ' '], // FIGURE SPACE — exactly a digit wide, exists to align numeric columns
  [0x2008, ' '],
  [0x2009, ' '],
  [0x200a, ' '],
  [0x202f, ' '], // NARROW NO-BREAK SPACE
  [0x205f, ' '], // MEDIUM MATHEMATICAL SPACE
  [0x3000, ' '], // IDEOGRAPHIC SPACE — the ordinary space of Japanese and Chinese
]);

export const ZERO_WIDTH = new Set([
  0x200b, // ZERO WIDTH SPACE
  0x200c, // ZERO WIDTH NON-JOINER      — context-dependent
  0x200d, // ZERO WIDTH JOINER          — context-dependent
  0x2060, // WORD JOINER
  0x2065, // reserved, Default_Ignorable
  0xfeff, // ZERO WIDTH NO-BREAK SPACE / BOM
  0x034f, // COMBINING GRAPHEME JOINER  — legitimate collation device
]);

/** U+2061..U+2064 are correct and intended in MathML and technical plain text. */
export const INVISIBLE_MATH = new Set([0x2061, 0x2062, 0x2063, 0x2064]);

export const BIDI_OPEN_EMBED = new Set([0x202a, 0x202b, 0x202d, 0x202e]);
export const BIDI_CLOSE_EMBED = 0x202c;
export const BIDI_OPEN_ISOLATE = new Set([0x2066, 0x2067, 0x2068]);
export const BIDI_CLOSE_ISOLATE = 0x2069;
/** Implicit marks. They never pair and are routine in real bidirectional text. */
export const BIDI_MARKS = new Set([0x200e, 0x200f, 0x061c]);

export function isBidiControl(cp: number): boolean {
  return (
    BIDI_OPEN_EMBED.has(cp) ||
    cp === BIDI_CLOSE_EMBED ||
    BIDI_OPEN_ISOLATE.has(cp) ||
    cp === BIDI_CLOSE_ISOLATE ||
    BIDI_MARKS.has(cp)
  );
}

/** Short display names for the bidi controls, used for the ⟦RLO⟧ substitution. */
export const BIDI_NAMES = new Map<number, string>([
  [0x202a, 'LRE'],
  [0x202b, 'RLE'],
  [0x202c, 'PDF'],
  [0x202d, 'LRO'],
  [0x202e, 'RLO'],
  [0x2066, 'LRI'],
  [0x2067, 'RLI'],
  [0x2068, 'FSI'],
  [0x2069, 'PDI'],
  [0x200e, 'LRM'],
  [0x200f, 'RLM'],
  [0x061c, 'ALM'],
]);

/**
 * Letters and symbols that render as nothing.
 *
 * The point of this group is that General_Category says L* or So, so they pass every "is this
 * alphanumeric" check, every username validator and every `\w` — and show nothing. U+3164 is the
 * classic invisible-username and invisible-filename trick.
 */
export const INVISIBLE_FILLERS = new Set([
  0x115f, // HANGUL CHOSEONG FILLER
  0x1160, // HANGUL JUNGSEONG FILLER
  0x3164, // HANGUL FILLER
  0xffa0, // HALFWIDTH HANGUL FILLER
  0x17b4, // KHMER VOWEL INHERENT AQ
  0x17b5, // KHMER VOWEL INHERENT AA
  0x180e, // MONGOLIAN VOWEL SEPARATOR
  0x2800, // BRAILLE PATTERN BLANK — So, not whitespace, not ignorable, completely invisible
]);

export const INTERLINEAR = new Set([0xfff9, 0xfffa, 0xfffb]);

export const LINE_SEPARATORS = new Set([
  0x2028, // LINE SEPARATOR
  0x2029, // PARAGRAPH SEPARATOR
  0x0085, // NEXT LINE
]);

/**
 * Typographic substitutions, and the ASCII they are usually standing in for.
 *
 * HONESTY GATE. These are in scope because they genuinely break CSV imports, JSON, shell commands
 * and pasted code — which IS the user's problem. They are NOT evidence of anything about who or
 * what wrote the text: Word autocorrect, macOS smart substitutions, iOS keyboards, InDesign and
 * every CMS typographer filter produce all of them. Nothing in this file, and nothing in the UI,
 * may attach a provenance to them.
 */
export const TYPOGRAPHIC = new Map<number, string>([
  [0x2018, "'"],
  [0x2019, "'"],
  [0x201a, "'"],
  [0x201b, "'"],
  [0x201c, '"'],
  [0x201d, '"'],
  [0x201e, '"'],
  [0x2013, '-'],
  [0x2014, '--'],
  [0x2010, '-'],
  [0x2011, '-'],
  [0x2012, '-'],
  [0x2026, '...'],
  [0x2032, "'"],
  [0x2033, '"'],
  [0x00b4, "'"],
  [0x02bc, "'"],
]);

export function isPrivateUse(cp: number): boolean {
  return (
    (cp >= 0xe000 && cp <= 0xf8ff) ||
    (cp >= 0xf0000 && cp <= 0xffffd) ||
    (cp >= 0x100000 && cp <= 0x10fffd)
  );
}

export function isTagChar(cp: number): boolean {
  return cp >= TAG_BLOCK_START && cp <= TAG_BLOCK_END;
}

export function isVariationSelector(cp: number): boolean {
  return (cp >= VS_BMP_START && cp <= VS_BMP_END) || (cp >= VS_SUP_START && cp <= VS_SUP_END);
}

/** C0 and C1 controls, and DEL — but never TAB, LF or CR. */
export function isBadControl(cp: number): boolean {
  if (cp === 0x09 || cp === 0x0a || cp === 0x0d) return false;
  return cp <= 0x1f || cp === 0x7f || (cp >= 0x80 && cp <= 0x9f);
}

export function isUnassigned(cp: number): boolean {
  return generalCategory(cp) === 'Cn' && !isNoncharacter(cp);
}

/**
 * Does charsight have anything to say about this code point at all?
 *
 * Deliberately excludes ASCII and the vast bulk of ordinary letters — the scanner walks every code
 * point in the file, so this is the hot path, and it must be cheap and it must be quiet.
 */
export function isCandidate(cp: number): boolean {
  if (cp < 0x80) return isBadControl(cp);
  if (isTagChar(cp)) return true;
  if (isVariationSelector(cp)) return true;
  if (ZERO_WIDTH.has(cp)) return true;
  if (INVISIBLE_MATH.has(cp)) return true;
  if (isBidiControl(cp)) return true;
  if (cp === 0x00ad) return true;
  if (SPACE_LIKE.has(cp)) return true;
  if (LINE_SEPARATORS.has(cp)) return true;
  if (isBadControl(cp)) return true;
  if (isPrivateUse(cp)) return true;
  if (INVISIBLE_FILLERS.has(cp)) return true;
  if (INTERLINEAR.has(cp)) return true;
  if (isNoncharacter(cp)) return true;
  if (TYPOGRAPHIC.has(cp)) return true;
  if (isDeprecated(cp)) return true;
  if (isPrependedMark(cp)) return false; // required to be VISIBLE — not hidden text
  if (isUnassigned(cp)) return true;
  return false;
}

/** The bucket a code point falls in, before any context is applied. */
export function categoryOf(cp: number): CategoryId | null {
  if (isTagChar(cp)) return 'tags';
  if (isVariationSelector(cp)) return 'vs-smuggling';
  if (isBidiControl(cp)) return 'bidi';
  if (cp === 0x00ad) return 'soft-hyphen';
  if (ZERO_WIDTH.has(cp) || INVISIBLE_MATH.has(cp)) return 'zero-width';
  if (SPACE_LIKE.has(cp)) return 'whitespace';
  if (LINE_SEPARATORS.has(cp)) return 'line-separator';
  if (isBadControl(cp)) return 'control';
  if (INVISIBLE_FILLERS.has(cp)) return 'invisible-filler';
  if (INTERLINEAR.has(cp)) return 'interlinear';
  if (isPrivateUse(cp)) return 'pua';
  if (isNoncharacter(cp) || isUnassigned(cp)) return 'noncharacter';
  if (TYPOGRAPHIC.has(cp)) return 'typographic';
  if (isDeprecated(cp)) return 'deprecated';
  return null;
}

export interface CategoryMeta {
  id: CategoryId;
  label: string;
  tier: Tier;
  /** One line, in the UI, above the findings in this group. */
  blurb: string;
}

export const CATEGORY_META: Record<CategoryId, CategoryMeta> = {
  tags: {
    id: 'tags',
    label: 'Tag characters',
    tier: 1,
    blurb:
      'Plane-14 tag characters mirror printable ASCII one-for-one and render as nothing anywhere, but a language model reads them as text. This is how instructions are smuggled into a file you are about to paste into an agent.',
  },
  'vs-smuggling': {
    id: 'vs-smuggling',
    label: 'Variation selectors',
    tier: 1,
    blurb:
      'The 256 variation selectors encode one byte each, so a chain of them after any character hides an arbitrary byte string. They are Mn, not Cf, so a filter that strips format characters misses them entirely.',
  },
  'zero-width': {
    id: 'zero-width',
    label: 'Zero-width characters',
    tier: 2,
    blurb:
      'Invisible, survive copy and paste, and break string equality, CSV parsing, JSON keys, package-name matching and diffs.',
  },
  bidi: {
    id: 'bidi',
    label: 'Bidirectional controls',
    tier: 2,
    blurb:
      'Reorder what a human sees without changing the order a compiler, parser or agent reads — the Trojan Source attack (CVE-2021-42574). Only unpaired controls are reported: balanced isolates around a name in right-to-left prose are correct practice.',
  },
  'soft-hyphen': {
    id: 'soft-hyphen',
    label: 'Soft hyphens',
    tier: 3,
    blurb:
      'Invisible unless a line break falls on one. Breaks identifier equality, URL comparison and grep — and splits a keyword past a naive filter.',
  },
  whitespace: {
    id: 'whitespace',
    label: 'Unusual spaces',
    tier: 3,
    blurb:
      'Look like a space and are not one. This is the usual cause of a pasted command that will not run, a CSV column that will not match, and a YAML file that will not parse.',
  },
  'line-separator': {
    id: 'line-separator',
    label: 'Line separators',
    tier: 3,
    blurb:
      'U+2028 and U+2029 are legal inside a JSON string but were line terminators in JavaScript before ES2019, so a JSON payload containing one can break a parser that reads it.',
  },
  control: {
    id: 'control',
    label: 'Control characters',
    tier: 2,
    blurb:
      'ESC enables terminal escape injection — text that repaints the line or fakes a prompt when a file is printed. NUL truncates in C-based parsers.',
  },
  pua: {
    id: 'pua',
    label: 'Private use area',
    tier: 2,
    blurb:
      'No assigned meaning. Renders as nothing, as a box, or — with a custom font — as any glyph the author chose, so it can impersonate any character at all. Nerd Fonts and Powerline icons live here too, which is why these are reported and never removed by default.',
  },
  'invisible-filler': {
    id: 'invisible-filler',
    label: 'Invisible letters',
    tier: 2,
    blurb:
      'Count as letters to identifier rules, username validators and \\w, yet render as nothing. U+3164 is the classic invisible-username trick.',
  },
  deprecated: {
    id: 'deprecated',
    label: 'Deprecated format characters',
    tier: 2,
    blurb:
      'Unicode says not to use them, so no conforming process has to honour them — which means implementations diverge, which is what makes them useful to an attacker.',
  },
  interlinear: {
    id: 'interlinear',
    label: 'Interlinear annotation',
    tier: 2,
    blurb:
      'Explicitly excluded from Default_Ignorable, yet invisible in most renderers — which is exactly why a check built only on that property misses them.',
  },
  noncharacter: {
    id: 'noncharacter',
    label: 'Noncharacters and unassigned',
    tier: 2,
    blurb:
      'Permanently unassigned and never valid for interchange. Their presence means the file passed through something that should have rejected it, or that someone is probing a parser.',
  },
  homoglyph: {
    id: 'homoglyph',
    label: 'Lookalike characters',
    tier: 4,
    blurb:
      'Visible, not hidden — the other half of Trojan Source (CVE-2021-42694). Two identifiers, URLs or package names that look identical and are different byte sequences.',
  },
  combining: {
    id: 'combining',
    label: 'Stacked combining marks',
    tier: 4,
    blurb:
      'Long runs of marks on one base overflow line boxes and hide adjacent text. Vietnamese, Thai, pointed Hebrew and Devanagari legitimately stack several, so the threshold is deliberately high.',
  },
  typographic: {
    id: 'typographic',
    label: 'Typographic substitutions',
    tier: 5,
    blurb:
      'Curly quotes, dashes and ellipses. In scope because they break CSV, JSON, shell commands and pasted code — and for no other reason. They say nothing about who or what wrote the text.',
  },
  'invalid-byte': {
    id: 'invalid-byte',
    label: 'Invalid bytes',
    tier: 3,
    blurb:
      'Bytes that are not valid in this encoding. Usually a file that is really Windows-1252 or Latin-1 rather than UTF-8 — an encoding problem, not hidden text.',
  },
};
